import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {isTrackReference, useTracks, type TrackReference, type TrackReferenceOrPlaceholder} from '@livekit/components-react';
import {Track} from 'livekit-client';
import {orderForStage, type ParticipantView} from '@phonehq/rtc-core';
import {ParticipantViewsContext} from '../../groupCalls/components/callContext';
import {useActiveSpeaker} from '../../groupCalls/hooks/useActiveSpeaker';

const SOURCES = [
    {source: Track.Source.Camera, withPlaceholder: true},
    {source: Track.Source.ScreenShare, withPlaceholder: false},
];

export interface GroupCameraTile {
    readonly trackRef: TrackReferenceOrPlaceholder;
    readonly view: ParticipantView;
}

export interface GroupScreenShare {
    readonly trackRef: TrackReference;
    readonly view: ParticipantView;
}

export interface GroupTracks {
    readonly cameras: readonly GroupCameraTile[];
    readonly screen?: GroupScreenShare;
    readonly speakerIdentity?: string;
    readonly speaking: ReadonlySet<string>;
    readonly participants: readonly ParticipantView[];
}

export const SPEAKING_HOLD_MS = 900;

export interface SpeakingState {
    readonly speaking: ReadonlySet<string>;
    readonly lastSpoke: ReadonlyMap<string, number>;
    readonly recheckAt?: number;
}

export const speakingWithHold = (
    participants: readonly ParticipantView[],
    lastSpoke: ReadonlyMap<string, number>,
    now: number,
    holdMs = SPEAKING_HOLD_MS,
): SpeakingState => {
    const next = new Map<string, number>();
    const speaking = new Set<string>();
    let recheckAt: number | undefined;
    participants.forEach(view => {
        if (!view.microphoneEnabled) {
            return;
        }
        const last = view.speaking ? now : lastSpoke.get(view.identity);
        if (last === undefined || now - last >= holdMs) {
            return;
        }
        next.set(view.identity, last);
        speaking.add(view.identity);
        if (!view.speaking) {
            recheckAt = Math.min(recheckAt ?? Number.POSITIVE_INFINITY, last + holdMs);
        }
    });
    return {speaking, lastSpoke: next, recheckAt};
};

const useSpeaking = (participants: readonly ParticipantView[]): ReadonlySet<string> => {
    const lastSpoke = useRef<ReadonlyMap<string, number>>(new Map());
    const [recheckAt, setRecheckAt] = useState(0);
    const state = useMemo(() => speakingWithHold(participants, lastSpoke.current, Math.max(Date.now(), recheckAt)), [participants, recheckAt]);

    useEffect(() => {
        lastSpoke.current = state.lastSpoke;
        if (state.recheckAt === undefined) {
            return;
        }
        const due = state.recheckAt;
        const timer = setTimeout(() => setRecheckAt(due), Math.max(0, due - Date.now()) + 20);
        return () => clearTimeout(timer);
    }, [state]);

    return state.speaking;
};

export const useGroupTracks = (): GroupTracks => {
    const views = useContext(ParticipantViewsContext);
    const trackRefs = useTracks(SOURCES, {onlySubscribed: false});
    const participants = useMemo(() => [...views.values()], [views]);
    const speakerIdentity = useActiveSpeaker(participants);
    const speaking = useSpeaking(participants);

    return useMemo(() => {
        const rank = new Map(orderForStage(participants).map((view, index) => [view.identity, index]));
        const known = trackRefs.filter(trackRef => views.has(trackRef.participant.identity));
        const cameras = known
            .filter(trackRef => trackRef.source === Track.Source.Camera)
            .sort((left, right) => (rank.get(left.participant.identity) ?? 0) - (rank.get(right.participant.identity) ?? 0))
            .map(trackRef => ({trackRef, view: views.get(trackRef.participant.identity) as ParticipantView}));
        const screens = known
            .filter((trackRef): trackRef is TrackReference => trackRef.source === Track.Source.ScreenShare && isTrackReference(trackRef))
            .map(trackRef => ({trackRef, view: views.get(trackRef.participant.identity) as ParticipantView}));
        const screen = screens.find(entry => !entry.view.isLocal) ?? screens[0];
        return {cameras, screen, speakerIdentity, speaking, participants};
    }, [participants, speakerIdentity, speaking, trackRefs, views]);
};

export interface TileLayout {
    readonly columns: number;
    readonly width: number;
    readonly height: number;
}

export const TILE_ASPECT = {min: 4 / 3, max: 16 / 9} as const;

export const fitTiles = (count: number, width: number, height: number, gap: number): TileLayout => {
    let best: TileLayout = {columns: 1, width: 0, height: 0};
    if (count <= 0 || width <= 0 || height <= 0) {
        return best;
    }
    for (let columns = 1; columns <= count; columns++) {
        const rows = Math.ceil(count / columns);
        const cellWidth = (width - gap * (columns - 1)) / columns;
        const cellHeight = (height - gap * (rows - 1)) / rows;
        const tileWidth = Math.floor(Math.min(cellWidth, cellHeight * TILE_ASPECT.max));
        const tileHeight = Math.floor(Math.min(cellHeight, tileWidth / TILE_ASPECT.min));
        if (tileWidth > 0 && tileHeight > 0 && tileWidth * tileHeight > best.width * best.height) {
            best = {columns, width: tileWidth, height: tileHeight};
        }
    }
    return best;
};

export const chooseSlots = (
    order: readonly string[],
    previous: readonly string[],
    speaker: string | undefined,
    lastSpoke: ReadonlyMap<string, number>,
    limit: number,
): string[] => {
    const present = new Set(order);
    const queue = order.filter(identity => !previous.includes(identity));
    const slots = previous.slice(0, limit).flatMap(identity => {
        const next = present.has(identity) ? identity : queue.shift();
        return next ? [next] : [];
    });
    while (slots.length < limit && queue.length > 0) {
        slots.push(queue.shift() as string);
    }
    if (speaker && present.has(speaker) && !slots.includes(speaker) && slots.length > 0) {
        const quietest = slots.reduce((best, identity, index) => ((lastSpoke.get(identity) ?? 0) < (lastSpoke.get(slots[best]) ?? 0) ? index : best), 0);
        slots[quietest] = speaker;
    }
    return slots;
};

export const useStableTiles = <T extends {readonly view: ParticipantView}>(tiles: readonly T[], speaker: string | undefined, limit: number): T[] => {
    const slots = useRef<string[]>([]);
    const lastSpoke = useRef(new Map<string, number>());
    const tick = useRef(0);
    return useMemo(() => {
        if (speaker) {
            tick.current += 1;
            lastSpoke.current.set(speaker, tick.current);
        }
        slots.current = chooseSlots(tiles.map(tile => tile.view.identity), slots.current, speaker, lastSpoke.current, limit);
        const byIdentity = new Map(tiles.map(tile => [tile.view.identity, tile]));
        return slots.current.flatMap(identity => {
            const tile = byIdentity.get(identity);
            return tile ? [tile] : [];
        });
    }, [limit, speaker, tiles]);
};
